import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Plus, Edit2, Trash2, X, Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import api, { uploadRequestConfig } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    date: '',
    description: ''
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/admin/holidays');
      const allHolidays = response.data || [];
      const sortedHolidays = [...allHolidays].sort((a, b) => new Date(a.date) - new Date(b.date));
      setHolidays(sortedHolidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const suffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${suffix(day)} ${month} ${year}`;
  };

  const formatWeekDay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const handleOpenModal = (holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setFormData({
        date: new Date(holiday.date).toISOString().split('T')[0],
        description: holiday.description
      });
    } else {
      setEditingHoliday(null);
      setFormData({ date: '', description: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ date: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingHoliday) {
        await api.put(`/admin/holiday/${editingHoliday._id}`, formData);
        toast.success('Holiday updated successfully!');
      } else {
        await api.post('/admin/holiday', formData);
        toast.success('Holiday added successfully!');
      }
      handleCloseModal();
      fetchHolidays();
    } catch (error) {
      const message = error.response?.data?.message || `Failed to ${editingHoliday ? 'update' : 'add'} holiday`;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (holidayId) => {
    try {
      await api.delete(`/admin/holiday/${holidayId}`);
      toast.success('Holiday deleted successfully!');
      setDeleteConfirm(null);
      fetchHolidays();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete holiday';
      toast.error(message);
    }
  };

  const handleDownloadExcel = () => {
    if (!holidays.length) {
      toast.warning('No holidays to download.');
      return;
    }

    const rows = holidays.map((holiday, index) => {
      const d = new Date(holiday.date);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return {
        'S.No': index + 1,
        Date: `${dd}/${mm}/${yyyy}`,
        'Week Day': formatWeekDay(holiday.date),
        'Holiday Name': holiday.description,
        Approval: 1,
        'Is Recurring': holiday.isRecurring ? 'yes' : 'no'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet['!cols'] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 12 },
      { wch: 40 },
      { wch: 10 },
      { wch: 14 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Holidays');

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Holiday_List_${stamp}.xlsx`);
    toast.success('Holiday list downloaded as Excel.');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const handleFileUpload = async (file) => {
    setUploadLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/holidays/bulk-upload', formData, uploadRequestConfig());

      setUploadResults(response.data);
      setShowUploadResults(true);
      fetchHolidays();

      if (response.data.summary.added > 0) {
        toast.success(
          `Holiday list replaced: ${response.data.summary.added} holiday(s) imported (Approval = 1).`
        );
      } else {
        toast.warning(
          'No rows had Approval = 1. Previous holidays were cleared; the list may be empty.'
        );
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to upload file';
      toast.error(message);
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2 max-w-3xl">
          <h1 className="text-2xl font-bold text-gray-900">Holiday List (FY 2026-27)</h1>
          <p className="text-gray-600">Manage company holidays and special days for FY 2026-27</p>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-800">Excel upload replaces the entire holiday list.</span>{' '}
            Your file must include an <span className="font-medium">Approval</span> column; only rows with{' '}
            <span className="font-medium">Approval = 1</span> are saved.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <button
            type="button"
            onClick={handleDownloadExcel}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-5 w-5 mr-2" />
            Download Template
          </button>

          {/* Upload Excel Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadLoading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                Upload Excel
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Add Holiday Button */}
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* Holidays Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12">
            <LoadingSpinner size="lg" className="mx-auto" />
          </div>
        ) : holidays.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Week Day
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holiday Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {holidays.map((holiday, index) => (
                  <tr key={holiday._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 text-primary-600 mr-3" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(holiday.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                      {formatWeekDay(holiday.date)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 font-medium">{holiday.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <button
                          onClick={() => handleOpenModal(holiday)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                          title="Edit holiday"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(holiday._id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete holiday"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No holidays</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a new holiday.</p>
            <div className="mt-6">
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Holiday
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Holiday Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g., New Year's Day"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[100px]"
                >
                  {loading ? <LoadingSpinner size="sm" /> : editingHoliday ? 'Update' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete Holiday
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to delete this holiday? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Results Modal */}
      {showUploadResults && uploadResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Upload Results</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Full-replace upload: only rows with Approval = 1 are imported.
                </p>
              </div>
              <button
                onClick={() => setShowUploadResults(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{uploadResults.summary.total}</div>
                  <div className="text-sm text-blue-700 mt-1">Total Rows</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{uploadResults.summary.added}</div>
                  <div className="text-sm text-green-700 mt-1">Added</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{uploadResults.summary.skipped}</div>
                  <div className="text-sm text-yellow-700 mt-1">Skipped</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{uploadResults.summary.errors}</div>
                  <div className="text-sm text-red-700 mt-1">Errors</div>
                </div>
              </div>

              {/* Added Holidays */}
              {uploadResults.details.added.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    Successfully Added ({uploadResults.details.added.length})
                  </h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                    {uploadResults.details.added.map((item, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <span className="text-green-600 font-medium min-w-[60px]">Row {item.row}:</span>
                        <span className="text-gray-700 ml-2">
                          {item.date} - {item.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped Holidays */}
              {uploadResults.details.skipped.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-yellow-700 mb-3 flex items-center">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                    Skipped ({uploadResults.details.skipped.length})
                  </h4>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                    {uploadResults.details.skipped.map((item, idx) => (
                      <div key={idx} className="text-sm">
                        <div className="flex items-start">
                          <span className="text-yellow-600 font-medium min-w-[60px]">Row {item.row}:</span>
                          <span className="text-gray-700 ml-2">{item.date}</span>
                        </div>
                        <div className="text-yellow-700 text-xs ml-[68px] mt-1">{item.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {uploadResults.details.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    Errors ({uploadResults.details.errors.length})
                  </h4>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                    {uploadResults.details.errors.map((item, idx) => (
                      <div key={idx} className="text-sm">
                        <div className="flex items-start">
                          <span className="text-red-600 font-medium min-w-[60px]">Row {item.row}:</span>
                          <span className="text-red-700 ml-2">{item.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowUploadResults(false)}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayManagement;
